import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../components';
import DarkModeToggle from '../components/DarkModeToggle';
import { useAuthStore } from '../lib/auth';

export default function RegisterPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [error, setError] = useState('');
  const { register, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password strength (match backend requirements)
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      setError('Password must contain at least one special character');
      return;
    }

    try {
      await register({ email, password, fullName, workspaceName });

      // Check if workspace needs onboarding
      const { workspace, isAuthenticated } = useAuthStore.getState();

      console.log('Registration successful, auth state:', { workspace, isAuthenticated });

      if (!workspace || !isAuthenticated) {
        throw new Error('Registration completed but session not established');
      }

      if (!workspace.onboardingCompleted) {
        navigate('/onboarding');
      } else if (workspace.slug) {
        navigate(`/${workspace.slug}/dashboard`);
      } else {
        navigate('/login');
      }
    } catch (err: any) {
      console.error('Registration error:', err);

      // Handle duplicate email (409 Conflict)
      if (err.response?.status === 409) {
        setError(t('auth.register.emailExists'));
        return;
      }

      // Handle other errors
      setError(
        err.response?.data?.message || err.message || t('auth.register.error')
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4 transition-colors duration-300">
      <Card className="w-full max-w-md relative">
        <DarkModeToggle />

        <div className="mb-8 text-center">
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 dark:hidden"
          />
          <img
            src="/logo-white.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 hidden dark:block"
          />
          <h1 className="text-3xl font-bold mb-2 dark:text-gray-100">{t('auth.register.title')}</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('app.tagline')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="text"
            label={t('auth.register.fullName')}
            placeholder={t('auth.register.fullNamePlaceholder')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            autoComplete="name"
            autoFocus
          />

          <Input
            type="email"
            label={t('auth.register.email')}
            placeholder={t('auth.register.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />

          <Input
            type="password"
            label={t('auth.register.password')}
            placeholder={t('auth.register.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />

          <Input
            type="text"
            label={t('auth.register.workspaceName')}
            placeholder={t('auth.register.workspaceNamePlaceholder')}
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            required
          />

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
              <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full"
          >
            {t('auth.register.submit')}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t-2 border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            {t('auth.register.hasAccount')}{' '}
            <a
              href="/login"
              className="font-semibold text-black dark:text-gray-100 hover:underline"
            >
              {t('auth.register.login')}
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
}
